import React, { useState } from 'react';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { FileArchiveContext } from 'contexts/FileArchiveContext';
import {
	Wrapper,
	Sidebar,
	Content,
	Section,
	Title,
	Description,
	TabsWrapper,
	BottomSection,
} from './AppendData.styles';
import SidebarNavLink from '../components/SidebarNavLink';
import { Route, Redirect, Switch } from 'react-router-dom';
import { ApButtonDanger } from '@alixpartners/ui-components';
import { CONFIG } from './routes';
import { UPLOAD_STATUS, FILE_TYPES, SPEND_TYPE } from '../constants';
import DirectIndirectSpend from '../DirectIndirectSpend';
import { deleteAllData } from 'utils/apiRequests';
import DeleteAllDataModal from '../components/DeleteAllDataModal';
import {
	useHistory,
	useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';

const getDefaultStatusIcon = fileList =>
	fileList.length > 0 ? 'check' : 'cloud_upload';

const fileTypes = {
	[SPEND_TYPE.INDIRECT]: FILE_TYPES.UNCLASSIFIED_INDIRECT,
	[SPEND_TYPE.DIRECT]: FILE_TYPES.UNCLASSIFIED_DIRECT,
};

export const AppendData = ({ permissions, status, fileArchive }) => {
	const history = useHistory();
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const canDeleteAllData = permissions.includes(
		PERMISSIONS.TransactionsDeleteAll
	);
	const isProcessing = status.state === UPLOAD_STATUS.CLASSIFYING;
	const firstAvailableRoute = CONFIG.find(({ required, name }) => {
		return (
			permissions.includes(required) &&
			(status.isDirectSpend === (name === SPEND_TYPE.DIRECT) || !isProcessing)
		);
	});
	const location = useLocation();
	const hasErrors =
		status.state === UPLOAD_STATUS.FAILED ||
		(status.errors && status.errors.length > 0);
	const hasNoData =
		UPLOAD_STATUS.NO_DATA === status.state && !status.isDataPublished;
	const isDataPublished = status.state === UPLOAD_STATUS.PUBLISHED;
	const currentStatusIcon = hasErrors
		? 'outline_error'
		: hasNoData
			? 'cloud_upload'
			: isDataPublished
				? 'check'
				: 'dynamic_feed';

	const getFileListByType = type =>
		fileArchive.filter(({ fileType }) => fileType.value === type);

	const handleDeleteAllData = () => {
		setIsDeleting(true);

		deleteAllData()
			.then(() => {
				setIsDeleting(false);
				setDeleteModalOpen(false);
			})
			.catch(() => {
				setIsDeleting(false);
			});
	};

	return (
		<Wrapper>
			<Sidebar>
				<Section>
					<Title>Data upload</Title>
					<Description>
						<p>Data upload process:</p>
						<p>1. Upload file into the system</p>
						<p>2. Configure set up</p>
						<p>3. Generate spend cube (automatically)</p>
					</Description>
				</Section>
				<TabsWrapper>
					{CONFIG.map(({ label, path, required, name }) => {
						if (required && !permissions.includes(required)) {
							return null;
						}

						return (
							<SidebarNavLink
								key={path}
								to={path}
								iconName={
									status.isDirectSpend === (name === SPEND_TYPE.DIRECT)
										? currentStatusIcon
										: getDefaultStatusIcon(getFileListByType(fileTypes[name]))
								}
								disabled={
									status.isDirectSpend !== (name === SPEND_TYPE.DIRECT) &&
									isProcessing
								}
							>
								{label}
							</SidebarNavLink>
						);
					})}
				</TabsWrapper>
				{canDeleteAllData && (
					<BottomSection>
						<Title>Delete data</Title>
						<Description>
							Data deletion clears direct and indirect spend data
						</Description>
						<ApButtonDanger
							iconName="delete"
							onClick={e => {
								e.preventDefault();

								setDeleteModalOpen(true);
							}}
						>
							Delete all data
						</ApButtonDanger>
					</BottomSection>
				)}
			</Sidebar>
			<Content>
				<Switch>
					{CONFIG.map(({ name, path, required }) => {
						if (
							!permissions.includes(required) ||
							(status.isDirectSpend !== (name === SPEND_TYPE.DIRECT) &&
								isProcessing)
						) {
							return null;
						}

						return (
							<Route
								key={path}
								path={path}
								render={() => (
									<DirectIndirectSpend
										isDirect={name === SPEND_TYPE.DIRECT}
										// We use one state api, for both direct/indirect spend
										// If the last uploaded file was indirect spend then
										// we emulate No Data state for direct spend
										normalUrlInfo={location.state}
										newhistory={history}
										status={{
											...status,
											...(status.isDirectSpend !== (name === SPEND_TYPE.DIRECT)
												? {
														state: UPLOAD_STATUS.NO_DATA,
														errors: [],
												  }
												: {}),
										}}
									/>
								)}
							/>
						);
					})}

					{firstAvailableRoute && <Redirect to={firstAvailableRoute.path} />}
				</Switch>
			</Content>
			{canDeleteAllData &&
				isDeleteModalOpen && (
					<DeleteAllDataModal
						onConfirm={handleDeleteAllData}
						onCancel={() => (!isDeleting ? setDeleteModalOpen(false) : null)}
						inProgress={isDeleting}
					/>
				)}
		</Wrapper>
	);
};

export default props => (
	<FileArchiveContext.Consumer>
		{({ fileArchive }) => (
			<UploadStateContext.Consumer>
				{({ status }) => (
					<PermissionsContext.Consumer>
						{({ permissions }) => (
							<AppendData
								{...props}
								permissions={permissions || []}
								status={status}
								fileArchive={fileArchive}
							/>
						)}
					</PermissionsContext.Consumer>
				)}
			</UploadStateContext.Consumer>
		)}
	</FileArchiveContext.Consumer>
);
